/**
 * lib/pult-layout.ts — the pult, variant B: every part of the board, where it stands, how big it
 * is, and on which tab it shows, in iPhone points.
 *
 * `.omc/wiki/pult-variant-b.md` «Почему Б», «Что такое Б». The owner chose variant B over the copy
 * of the Rane System One — «вариант Б как будто удобнее для пальцев»: the parts and the look of the
 * copy, every target at least 44 points (Apple's smallest for a finger), and what does not fit put
 * behind tabs, VINYL · PADS · LOOP on each deck, MIXER · FX · SCREEN on the mixer. The owner's
 * pictures (`docs/design/2026-11-03-pult-b/`) are drawn by a generator,
 * `.omc/wiki/raw/pult-generator-3/fingerlib.py`, and this module is that generator's numbers,
 * carried over call for call — `portrait_b()` to {@link PULT_LAYOUT}`.port` (418 × 790),
 * `landscape_b()` to `.land` (814 × 363) — points of the phone one to one, no stretch. The
 * generator draws one picture for each set of tabs; here the three sets are one table: a part every
 * set draws is in it once, a part of one tab says so (`shownOn`), in the generator's order, so that
 * what one set of tabs shows of the table is the generator's picture of it part for part.
 * `test/pult-layout.test.ts` lays the two side by side against a snapshot of the generator
 * (`test/fixtures/pult-generator.json`), in the three sets of the pictures, both ways the phone is
 * held, and holds every target to the 44 points. A change of the board is a new generator first,
 * then a new snapshot, then here.
 *
 * On its side the generator's board is 814 × 365 with a margin of 6 all round; the room between
 * Telegram's safe areas on an iPhone 15 Pro Max is 814 × 363 (task 53's probe), so the app's board
 * is the generator's a point higher, its margin 5 at the top and the foot: no target is made
 * smaller for it.
 *
 * The generator draws a fader's cap where its demo stands; the app moves it, so the way the cap
 * runs is the app's own (`travel`, {@link faderCap}): inside its slot whatever the value, and short
 * of the print at the slot's ends.
 *
 * ## Contract with the screen (`components/PultBoard.tsx`) and the sound
 *
 * Every control the hand can work has an `id`: `A.…` and `B.…` for a deck's, bare names for the
 * middle's (`load.A`, `xfader`, `fx.echo`, …), and a `side`, the deck whose sound it is. The ids,
 * kinds, sides and a fader's axis are the copy's, all 98 of them: the sound hears the id, not the
 * place (`screens/Pult.tsx`, `store/pult.ts`, `engine/player.ts`), so a new board changes the look
 * and not a sound. A tab says which part of the board shows it (`group`): deck A's LEVEL is deck A's
 * sound on the mixer's MIXER tab. Tabs are no control: the sound never hears them. Print, frames, the
 * letters and the separators have no id: they are the drawing.
 *
 * What the generator draws as a state of its demo — the tab lit, which of 33/45 is lit, START·STOP
 * on or off, HOT CUE and ECHO chosen, FX A up, the words on the pads — is the app's state, not the
 * layout: the table draws every such key unlit (`toggle`), and the board lights it from the stores.
 */

/** The two decks. */
export type DeckSide = 'A' | 'B';

/** Which deck a control belongs to, or the middle. */
export type PultSide = DeckSide | 'mid';

/** The two boards: phone on its side, and upright. */
export type PultOrientation = 'land' | 'port';

/** Points of the board, top-left corner and size. */
export interface PultRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** The faces of a key, as the generator names them (`sx.css`, `.b.<look>`). */
export type KeyLook = 'w' | 'dark' | 'sync' | 'cue' | 'ss' | 'blue' | 'violet' | 'orange' | 'red';

/** The faces of a knob: a small black one, the white EQ caps, the lit ring of the filter and FX. */
export type KnobLook = 'black' | 'white' | 'ring';

/** A deck's tabs: its platter, its pads, its loops. */
export type DeckTab = 'vinyl' | 'pads' | 'loop';

/** The mixer's tabs: the channels, the effects, the screen. */
export type MixerTab = 'mixer' | 'fx' | 'screen';

export type PultTab = DeckTab | MixerTab;

/** A deck's tabs, left to right, with their print. */
export const DECK_TABS = Object.freeze([
  ['vinyl', 'VINYL'],
  ['pads', 'PADS'],
  ['loop', 'LOOP'],
] as const);

/** The mixer's tabs, left to right, with their print. */
export const MIXER_TABS = Object.freeze([
  ['mixer', 'MIXER'],
  ['fx', 'FX'],
  ['screen', 'SCREEN'],
] as const);

/** A part of one tab: it shows while part `group` of the board has tab `tab` chosen. */
export interface PultShownOn {
  readonly group: PultSide;
  readonly tab: PultTab;
}

type PultDrawn =
  | {
      readonly kind: 'label';
      /** The centre of the print. */
      readonly x: number;
      readonly y: number;
      readonly fs: number;
      readonly text: string;
    }
  | {
      readonly kind: 'key';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
      readonly look: KeyLook;
      readonly text: string;
      /** The second, smaller line: RELOOP under LOOP ON/OFF, BEND under −. */
      readonly sub?: string | undefined;
      /** PARAMETER's two keys carry a triangle, not a word. */
      readonly glyph?: 'left' | 'right' | undefined;
      readonly fs: number;
      /** Lit by a state of the app (33/45, an effect chosen, AUTO): drawn unlit here. */
      readonly toggle?: true | undefined;
      /** The pad modes' words, set close to fit a key of 44. */
      readonly tight?: true | undefined;
    }
  | {
      readonly kind: 'tab';
      /** `A.tab.pads`, `tab.fx`: a tab is no control, but a finger's target all the same. */
      readonly id: string;
      readonly group: PultSide;
      readonly tab: PultTab;
      readonly rect: PultRect;
      readonly text: string;
      readonly fs: number;
    }
  | {
      readonly kind: 'knob';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
      readonly look: KnobLook;
    }
  | {
      readonly kind: 'fader';
      readonly id: string;
      readonly side: PultSide;
      readonly axis: 'v' | 'h';
      /** The slot the cap runs in. */
      readonly slot: PultRect;
      /** Where a finger takes it: the slot's whole length, as broad as the cap and never under 44. */
      readonly area: PultRect;
      /**
       * The way the cap's centre runs, 0 at its top or left end, as broad as `area`: the slot less
       * half a cap at each end, and less the print at an end (the tempo's «−» and «+»), so that the
       * cap stays in its slot and off the print (`.omc/wiki/pult-ekran.md` «Фейдеры»).
       */
      readonly travel: PultRect;
      /** The cap's size; where it stands along `travel` is the app's state ({@link faderCap}). */
      readonly capW: number;
      readonly capH: number;
      /** The scale beside a vertical fader. */
      readonly ticks: PultRect | undefined;
    }
  | {
      readonly kind: 'platter';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
    }
  | {
      readonly kind: 'pad';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
      /** 0–7, the colour of the pad from {@link PAD_COLOURS}. */
      readonly colour: number;
      /** The size of the words on it: what the screen over the copy's pads said of its cell. */
      readonly fs: number;
    }
  | {
      readonly kind: 'paddle';
      readonly id: string;
      readonly side: PultSide;
      readonly rect: PultRect;
    }
  | { readonly kind: 'screen'; readonly rect: PultRect }
  | {
      readonly kind: 'meter';
      readonly rect: PultRect;
      /** Lights in each of MAIN's four columns. */
      readonly segments: number;
    }
  | {
      /** Upright, the deck a row of the mixer's knobs is: a square of the deck's colour. */
      readonly kind: 'letter';
      readonly deck: DeckSide;
      readonly rect: PultRect;
      readonly fs: number;
    }
  | { readonly kind: 'sep'; readonly rect: PultRect };

/** A part of the board; without `shownOn`, on every tab. */
export type PultPart = PultDrawn & { readonly shownOn?: PultShownOn | undefined };

/** A fader of the board. */
export type PultFader = Extract<PultPart, { kind: 'fader' }>;

/**
 * Where a fader's cap stands at `value`, 0…1, in points of the board: its centre `value` of the way
 * along `travel`. The finger moves the value by its way over the length of `travel` on the screen
 * (`components/PultBoard.tsx`), so the cap goes with the finger one to one.
 */
export function faderCap(part: PultFader, value: number): PultRect {
  const { travel, capW, capH } = part;
  return part.axis === 'v'
    ? {
        x: travel.x + (travel.w - capW) / 2,
        y: travel.y + value * travel.h - capH / 2,
        w: capW,
        h: capH,
      }
    : {
        x: travel.x + value * travel.w - capW / 2,
        y: travel.y + (travel.h - capH) / 2,
        w: capW,
        h: capH,
      };
}

/** The size of the «0» by a tempo fader, in points of the board ({@link faderZero}). */
export const ZERO_FS = 8;

/**
 * The «0» by a vertical fader with its scale on its left (the tempo's), in points of the board:
 * the print over the scale and a line from it across the slot to the end of the cap's own line,
 * centred where the cap's centre is at the middle of its way — the tempo's 0 %, as
 * {@link faderCap} puts it. The owner could not find zero by eye or by finger (2026-11-04): the
 * line lies on the cap's when the cap is there.
 */
export function faderZero(part: PultFader): PultRect | undefined {
  if (part.axis !== 'v' || part.ticks === undefined) return undefined;
  const { travel, ticks, capW } = part;
  const left = ticks.x - 2;
  // The cap's line runs over the middle eight tenths of the cap (`styles/pult.css`, `.ppCap::after`).
  const right = travel.x + travel.w / 2 + capW * 0.4;
  return { x: left, y: travel.y + travel.h / 2 - ZERO_FS / 2, w: right - left, h: ZERO_FS };
}

export interface PultBoardLayout {
  readonly orientation: PultOrientation;
  readonly width: number;
  readonly height: number;
  readonly parts: readonly PultPart[];
}

/** The pads' colours, as the generator's `PADS`: face, highlight, shade, glow. */
export const PAD_COLOURS: readonly (readonly [string, string, string, string])[] = Object.freeze([
  ['#ff3b30', '#ffa198', '#b3140c', 'rgba(255,59,48,.7)'],
  ['#ff8a3c', '#ffc996', '#c2500e', 'rgba(255,138,60,.65)'],
  ['#6a6cff', '#b9baff', '#3a33c9', 'rgba(106,108,255,.7)'],
  ['#ffe14a', '#fff4ad', '#c4a10c', 'rgba(255,225,74,.55)'],
  ['#3fd65a', '#acf6b7', '#14902a', 'rgba(63,214,90,.65)'],
  ['#ff5fd0', '#ffb8ec', '#bd2296', 'rgba(255,95,208,.65)'],
  ['#33dbe0', '#aaf6f8', '#0d979b', 'rgba(51,219,224,.65)'],
  ['#a374ff', '#d8c2ff', '#6936d4', 'rgba(163,116,255,.65)'],
]);

/** The four pad modes, left to right, with the ids their keys carry and their print. */
export const PAD_MODES = Object.freeze([
  ['hotcue', 'HOT CUE'],
  ['roll', 'ROLL'],
  ['sampler', 'SAMPLER'],
  ['loop', 'LOOP'],
] as const);

/** Task 60: a pad mode, as its key's id ends: `A.mode.hotcue`. */
export type PadMode = (typeof PAD_MODES)[number][0];

/** The effect keys of the FX tab, left to right. */
const FX_KEYS = Object.freeze([
  ['echo', 'ECHO'],
  ['reverb', 'REVERB'],
  ['flanger', 'FLANGER'],
  ['phaser', 'PHASER'],
] as const);

/** The screen's three views, as the keys between the two LOADs name them. */
export const SCREEN_VIEWS = Object.freeze([
  ['queue', 'QUEUE'],
  ['library', 'LIBRARY'],
  ['view', 'VIEW'],
] as const);

/** Apple's smallest target for a finger, in points: the generator's `MIN`. */
const MIN = 44;
/** The main print on a key, and the generator's second print size. */
const LABEL = 11;
const SMALL = 9;

/** Two decimals, as the generator writes them into its page. */
function r2(value: number): number {
  return Math.round(value * 100) / 100 + 0;
}

interface KeyExtra {
  readonly sub?: string;
  readonly glyph?: 'left' | 'right';
  readonly toggle?: true;
  readonly tight?: true;
}

/**
 * The generator's `C` in points one to one: the board's parts in the order they are drawn, each
 * frozen as it is put. `dy` moves the whole board up (a point on its side); `on` marks what one tab
 * draws.
 */
class Sheet {
  readonly parts: PultPart[] = [];
  private shownOn: PultShownOn | undefined;

  constructor(private readonly dy: number) {}

  /** What `draw` puts shows only while part `group` has `tab` chosen. */
  on(group: PultSide, tab: PultTab, draw: () => void): void {
    this.shownOn = Object.freeze({ group, tab });
    draw();
    this.shownOn = undefined;
  }

  private put(part: PultDrawn): void {
    const shownOn = this.shownOn;
    this.parts.push(Object.freeze(shownOn === undefined ? part : { ...part, shownOn }));
  }

  private rect(x1: number, y1: number, x2: number, y2: number): PultRect {
    return Object.freeze({ x: r2(x1), y: r2(y1 + this.dy), w: r2(x2 - x1), h: r2(y2 - y1) });
  }

  label(cx: number, cy: number, text: string, size: number): void {
    this.put({ kind: 'label', x: r2(cx), y: r2(cy + this.dy), fs: size, text });
  }

  key(
    id: string,
    side: PultSide,
    [x1, y1, x2, y2]: readonly [number, number, number, number],
    look: KeyLook,
    text: string,
    size: number,
    extra: KeyExtra = {},
  ): void {
    this.put({
      kind: 'key',
      id,
      side,
      rect: this.rect(x1, y1, x2, y2),
      look,
      text,
      fs: size,
      ...extra,
    });
  }

  /** The generator's `tabs`: a row of three, 4 points apart. */
  tabs(
    group: PultSide,
    names: typeof DECK_TABS | typeof MIXER_TABS,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): void {
    const gap = 4;
    const w = (x2 - x1 - gap * (names.length - 1)) / names.length;
    names.forEach(([tab, text], i) => {
      const a = x1 + i * (w + gap);
      this.put({
        kind: 'tab',
        id: group === 'mid' ? `tab.${tab}` : `${group}.tab.${tab}`,
        group,
        tab,
        rect: this.rect(a, y1, a + w, y2),
        text,
        fs: LABEL,
      });
    });
  }

  knob(id: string, side: PultSide, cx: number, cy: number, d: number, look: KnobLook): void {
    this.put({
      kind: 'knob',
      id,
      side,
      rect: this.rect(cx - d / 2, cy - d / 2, cx + d / 2, cy + d / 2),
      look,
    });
  }

  /**
   * `ends`: the print beside the slot's top and foot (`y` of the label's centre, a line of `LABEL`)
   * that the cap would cover there — the tempo's «−» and «+», set where its 44 broad cap runs.
   */
  vfader(
    id: string,
    side: PultSide,
    cx: number,
    y1: number,
    y2: number,
    [capW, capH, slotW]: readonly [number, number, number],
    ticks: 'l' | 'r',
    ends?: readonly [number, number],
  ): void {
    const tx = ticks === 'l' ? cx - capW / 2 - 3.2 : cx + capW / 2 + 1.2;
    // The channel faders' caps are 40 broad: the finger's track is the 44 of a target all the same.
    const breadth = Math.max(capW, MIN);
    // The cap stops as short at both ends, so that the tempo's 0 % is the middle of its slot.
    const short =
      ends === undefined ? 0 : Math.max(0, ends[0] + LABEL / 2 - y1, y2 - ends[1] + LABEL / 2);
    this.put({
      kind: 'fader',
      id,
      side,
      axis: 'v',
      slot: this.rect(cx - slotW / 2, y1, cx + slotW / 2, y2),
      area: this.rect(cx - breadth / 2, y1, cx + breadth / 2, y2),
      travel: this.rect(
        cx - breadth / 2,
        y1 + short + capH / 2,
        cx + breadth / 2,
        y2 - short - capH / 2,
      ),
      capW,
      capH,
      ticks: this.rect(tx, y1, tx + 2, y2),
    });
  }

  hfader(
    id: string,
    x1: number,
    x2: number,
    cy: number,
    [capW, capH, slotH]: readonly [number, number, number],
  ): void {
    const breadth = Math.max(capH, MIN);
    this.put({
      kind: 'fader',
      id,
      side: 'mid',
      axis: 'h',
      slot: this.rect(x1, cy - slotH / 2, x2, cy + slotH / 2),
      area: this.rect(x1, cy - breadth / 2, x2, cy + breadth / 2),
      travel: this.rect(x1 + capW / 2, cy - breadth / 2, x2 - capW / 2, cy + breadth / 2),
      capW,
      capH,
      ticks: undefined,
    });
  }

  platter(side: DeckSide, cx: number, cy: number, d: number): void {
    this.put({
      kind: 'platter',
      id: `${side}.platter`,
      side,
      rect: this.rect(cx - d / 2, cy - d / 2, cx + d / 2, cy + d / 2),
    });
  }

  pad(side: DeckSide, n: number, x1: number, y1: number, x2: number, y2: number): void {
    this.put({
      kind: 'pad',
      id: `${side}.pad${String(n + 1)}`,
      side,
      rect: this.rect(x1, y1, x2, y2),
      colour: n,
      fs: SMALL,
    });
  }

  paddle(side: DeckSide, x1: number, y1: number, x2: number, y2: number): void {
    this.put({ kind: 'paddle', id: `${side}.fx`, side, rect: this.rect(x1, y1, x2, y2) });
  }

  screen(x1: number, y1: number, x2: number, y2: number): void {
    this.put({ kind: 'screen', rect: this.rect(x1, y1, x2, y2) });
  }

  meter(x1: number, y1: number, x2: number, y2: number): void {
    this.put({ kind: 'meter', rect: this.rect(x1, y1, x2, y2), segments: 18 });
  }

  letter(x: number, y: number, deck: DeckSide): void {
    this.put({ kind: 'letter', deck, rect: this.rect(x, y, x + 22, y + 22), fs: 13 });
  }

  sep(x1: number, y1: number, x2: number, y2: number): void {
    this.put({ kind: 'sep', rect: this.rect(x1, y1, x2, y2) });
  }
}

/* ── the deck's square slot: VINYL, PADS, LOOP ─────────────────────────────── */

/** `slot_pads`: the four modes over eight pads, PARAMETER under them. */
function slotPads(c: Sheet, d: DeckSide, x: number, y: number, s: number): void {
  const gap = 4;
  const w = (s - 3 * gap) / 4;
  const ph = (s - 2 * MIN - 3 * gap) / 2;
  PAD_MODES.forEach(([mode, name], i) => {
    const a = x + i * (w + gap);
    c.key(`${d}.mode.${mode}`, d, [a, y, a + w, y + MIN], 'w', name, SMALL, {
      toggle: true,
      tight: true,
    });
  });
  for (const r of [0, 1]) {
    const top = y + MIN + gap + r * (ph + gap);
    for (const i of [0, 1, 2, 3]) {
      const a = x + i * (w + gap);
      c.pad(d, r * 4 + i, a, top, a + w, top + ph);
    }
  }
  const bottom = y + s - MIN;
  c.key(`${d}.param.prev`, d, [x, bottom, x + w * 1.25, y + s], 'w', '', 16, { glyph: 'left' });
  c.key(`${d}.param.next`, d, [x + s - w * 1.25, bottom, x + s, y + s], 'w', '', 16, {
    glyph: 'right',
  });
  c.label(x + s / 2, bottom + MIN / 2, 'PARAMETER', SMALL);
}

/** `slot_loop`: three columns of four rows, JUMP SIZE taking the last column's two lower cells. */
function slotLoop(c: Sheet, d: DeckSide, x: number, y: number, s: number): void {
  const gap = 6;
  const cw = (s - 2 * gap) / 3;
  const rh = (s - 3 * gap) / 4;
  const col = (i: number): number => x + i * (cw + gap);
  const row = (j: number): number => y + j * (rh + gap);
  const cell = (i: number, j: number): [number, number, number, number] => [
    col(i),
    row(j),
    col(i) + cw,
    row(j) + rh,
  ];
  const id = (name: string): string => `${d}.${name}`;
  c.key(id('loop'), d, [col(0), row(0), col(1) + cw, row(0) + rh], 'orange', 'LOOP ON/OFF', LABEL, {
    sub: 'RELOOP',
  });
  c.key(id('slip'), d, cell(2, 0), 'blue', 'SLIP', LABEL);
  c.key(id('loop.half'), d, cell(0, 1), 'orange', '½', 14, { sub: 'IN' });
  c.key(id('loop.double'), d, cell(1, 1), 'orange', '2×', 14, { sub: 'OUT' });
  c.key(id('censor'), d, cell(2, 1), 'red', 'CENSOR', 10, { sub: 'REVERSE' });
  c.key(id('jump.back'), d, cell(0, 2), 'w', '◂ BEAT', 10, { sub: 'JUMP' });
  c.key(id('jump.fwd'), d, cell(1, 2), 'w', 'BEAT ▸', 10, { sub: 'JUMP' });
  const kcy = row(2) + (2 * rh + gap) / 2 - 7;
  c.knob(id('jumpsize'), d, col(2) + cw / 2, kcy, MIN, 'black');
  c.label(col(2) + cw / 2, kcy + MIN / 2 + 9, 'JUMP SIZE', SMALL);
  c.key(id('pullup'), d, cell(0, 3), 'violet', 'PULL UP', 10);
  c.key(id('echoout'), d, cell(1, 3), 'violet', 'ECHO OUT', 10);
}

/** `deck_slot`: the square under a deck's tabs, one tab's parts at a time. */
function deckSlot(c: Sheet, d: DeckSide, x: number, y: number, s: number): void {
  c.on(d, 'vinyl', () => {
    c.platter(d, x + s / 2, y + s / 2, s);
  });
  c.on(d, 'pads', () => {
    slotPads(c, d, x, y, s);
  });
  c.on(d, 'loop', () => {
    slotLoop(c, d, x, y, s);
  });
}

/* ── the mixer's slot: MIXER, FX, SCREEN, and its foot ─────────────────────── */

const EQ = Object.freeze([
  ['high', 'HIGH'],
  ['mid', 'MID'],
  ['low', 'LOW'],
] as const);

/** `mix_mixer_cols`, on its side: an EQ column each side, LEVEL over each channel fader, MAIN. */
function mixerColumns(c: Sheet, x: number, y: number, w: number, h: number): void {
  for (const [cx, d] of [
    [x + 26, 'A'],
    [x + w - 26, 'B'],
  ] as const) {
    EQ.forEach(([key, name], i) => {
      const cy = y + 22 + i * 60;
      c.knob(`${d}.${key}`, d, cx, cy, MIN, 'white');
      c.label(cx, cy + 30, name, SMALL);
    });
    const cy = y + 22 + 3 * 60 + 3;
    c.knob(`${d}.filter`, d, cx, cy, 50, 'ring');
    c.label(cx, cy + 33, 'FILTER', SMALL);
  }
  for (const [cx, d, num] of [
    [x + 89, 'A', '1'],
    [x + w - 89, 'B', '2'],
  ] as const) {
    c.knob(`${d}.level`, d, cx, y + 22, MIN, 'black');
    c.label(cx, y + 51, 'LEVEL', SMALL);
    c.vfader(`${d}.fader`, d, cx, y + 62, y + h - 24, [40, 20, 3.4], d === 'A' ? 'l' : 'r');
    c.label(cx, y + h - 11, num, LABEL);
  }
  c.label(x + w / 2, y + 51, 'MAIN', SMALL);
  c.meter(x + w / 2 - 9, y + 62, x + w / 2 + 9, y + h - 24);
}

/** `mix_mixer_rows`, upright: deck A's knobs in a row, deck B's under it, the faders at the right. */
function mixerRows(c: Sheet, x: number, y: number, w: number, h: number): void {
  const knobs = Object.freeze([
    ['level', 'LEVEL', 'black'],
    ['high', 'HIGH', 'white'],
    ['mid', 'MID', 'white'],
    ['low', 'LOW', 'white'],
    ['filter', 'FILTER', 'ring'],
  ] as const);
  for (const [r, d] of [
    [0, 'A'],
    [1, 'B'],
  ] as const) {
    const cy = y + 24 + r * 72;
    c.letter(x + 2, cy - 11, d);
    knobs.forEach(([key, name, look], i) => {
      const cx = x + 50 + i * 52;
      c.knob(`${d}.${key}`, d, cx, cy, MIN, look);
      c.label(cx, cy + 31, name, SMALL);
    });
  }
  const f1 = x + w - 94;
  const f2 = x + w - 24;
  for (const [cx, d, num] of [
    [f1, 'A', '1'],
    [f2, 'B', '2'],
  ] as const) {
    c.vfader(`${d}.fader`, d, cx, y + 4, y + h - 22, [40, 20, 3.4], d === 'A' ? 'l' : 'r');
    c.label(cx, y + h - 9, num, LABEL);
  }
  c.meter(f1 + 26, y + 4, f2 - 26, y + h - 22);
}

/** `mix_fx`: the four effects over the two paddles, BEATS and DEPTH between. */
function mixerEffects(c: Sheet, x: number, y: number, w: number, h: number): void {
  const gap = 4;
  const bw = (w - 3 * gap) / 4;
  FX_KEYS.forEach(([fx, name], i) => {
    const a = x + i * (bw + gap);
    c.key(`fx.${fx}`, 'mid', [a, y, a + bw, y + MIN], 'dark', name, 10, { toggle: true });
  });
  const mid = y + MIN + (h - MIN) / 2 - 6;
  const pw = 60;
  const ph = Math.min(96, h - MIN - 34);
  c.paddle('A', x, mid - ph / 2, x + pw, mid + ph / 2);
  c.label(x + pw / 2, mid + ph / 2 + 11, 'FX A', SMALL);
  c.paddle('B', x + w - pw, mid - ph / 2, x + w, mid + ph / 2);
  c.label(x + w - pw / 2, mid + ph / 2 + 11, 'FX B', SMALL);
  const kd = 50;
  const free = (w - 2 * pw - 2 * kd) / 3;
  for (const [cx, id, name] of [
    [x + pw + free + kd / 2, 'fx.beats', 'BEATS'],
    [x + w - pw - free - kd / 2, 'fx.depth', 'DEPTH'],
  ] as const) {
    c.knob(id, 'mid', cx, mid - 4, kd, 'ring');
    c.label(cx, mid + 33, name, SMALL);
  }
}

/** LOAD, the three views, LOAD: the keys of the screen, in the generator's order. */
const SCREEN_KEYS = Object.freeze([
  ['load.A', 'blue', '◂ LOAD'],
  ...SCREEN_VIEWS.map(([view, name]) => [`screen.${view}`, 'w', name] as const),
  ['load.B', 'blue', 'LOAD ▸'],
] as const);

function screenKey(c: Sheet, n: number, box: readonly [number, number, number, number]): void {
  const key = SCREEN_KEYS[n];
  if (key === undefined) return;
  const [id, look, text] = key;
  // The views are lit by the one shown; the LOADs are always blue.
  c.key(id, 'mid', box, look, text, SMALL, look === 'w' ? { toggle: true } : {});
}

/** `mix_screen_row`, on its side: the five keys in a row over the screen, as on the device. */
function screenRow(c: Sheet, x: number, y: number, w: number, h: number): void {
  const gap = 4;
  const bw = (w - 4 * gap) / 5;
  SCREEN_KEYS.forEach((_, i) => {
    const a = x + i * (bw + gap);
    screenKey(c, i, [a, y, a + bw, y + MIN]);
  });
  c.screen(x, y + MIN + 6, x + w, y + h);
}

/** `mix_screen_cols`, upright: the keys in a column each side of the screen. */
function screenColumns(c: Sheet, x: number, y: number, w: number, h: number): void {
  const cw = 66;
  const gap = 6;
  // ◂ LOAD, QUEUE, LIBRARY at the left; LOAD ▸ and VIEW at the right.
  for (const [n, i] of [
    [0, 0],
    [1, 1],
    [2, 2],
  ] as const) {
    const a = y + i * (MIN + gap);
    screenKey(c, n, [x, a, x + cw, a + MIN]);
  }
  for (const [n, i] of [
    [4, 0],
    [3, 1],
  ] as const) {
    const a = y + i * (MIN + gap);
    screenKey(c, n, [x + w - cw, a, x + w, a + MIN]);
  }
  c.screen(x + cw + gap, y, x + w - cw - gap, y + h);
}

/** `bottom_row`: AUTO, the crossfader and PRESET — on every tab of the mixer. */
function mixerFoot(c: Sheet, x: number, y: number, w: number, h: number): void {
  const bw = w < 300 ? 50 : 60;
  c.key('auto', 'mid', [x, y, x + bw, y + h], 'blue', 'AUTO', 10, {
    sub: 'АВТОМИКС',
    toggle: true,
  });
  c.key('preset', 'mid', [x + w - bw, y, x + w, y + h], 'dark', 'PRESET', 10, { sub: 'DUB OUT' });
  const a = x + bw + 20;
  const b = x + w - bw - 20;
  c.label(a - 11, y + h / 2, 'A', LABEL);
  c.label(b + 11, y + h / 2, 'B', LABEL);
  c.hfader('xfader', a, b, y + h / 2, [22, MIN, 3.4]);
}

/** The mixer's tabs, one slot's parts per tab, and its foot. `foot` is the foot's height. */
function mixer(c: Sheet, x: number, y: number, w: number, h: number, upright: boolean): void {
  c.tabs('mid', MIXER_TABS, x, y, x + w, y + MIN);
  const foot = upright ? 52 : 48;
  const sy = y + 50;
  const sh = h - 50 - 6 - foot;
  c.on('mid', 'mixer', () => {
    (upright ? mixerRows : mixerColumns)(c, x, sy, w, sh);
  });
  c.on('mid', 'fx', () => {
    mixerEffects(c, x, sy, w, sh);
  });
  c.on('mid', 'screen', () => {
    (upright ? screenColumns : screenRow)(c, x, sy, w, sh);
  });
  mixerFoot(c, x, y + h - foot, w, foot);
}

/* ── phone on its side: `landscape_b()`, 814 × 363 ─────────────────────────── */

/** `l_deck_b`: one deck on its side — the slot, the 33/45 and pitch column, the transport under. */
function landDeck(c: Sheet, d: DeckSide, x0: number, y0: number): void {
  const id = (name: string): string => `${d}.${name}`;
  const s = 200;
  c.tabs(d, DECK_TABS, x0, y0, x0 + s, y0 + MIN);
  const rx = x0 + s + 8;
  const rw = 58;
  c.key(id('keylock'), d, [rx, y0, rx + rw, y0 + MIN], 'blue', 'KEYLOCK', SMALL);
  deckSlot(c, d, x0, y0 + 50, s);
  // 33 and 45 change the record, as on the turntable; RESET at the foot of the pitch
  c.key(id('rpm33'), d, [rx, y0 + 50, rx + rw, y0 + 94], 'w', '33', 13, { toggle: true });
  c.key(id('rpm45'), d, [rx, y0 + 100, rx + rw, y0 + 144], 'w', '45', 13, { toggle: true });
  c.label(rx + rw - 6, y0 + 157, '−', LABEL);
  c.label(rx + rw - 6, y0 + 298, '+', LABEL);
  c.vfader(id('pitch'), d, rx + rw / 2, y0 + 152, y0 + 303, [MIN, 22, 4], 'l', [
    y0 + 157,
    y0 + 298,
  ]);
  c.key(id('reset'), d, [rx, y0 + 309, rx + rw, y0 + 353], 'w', '0 %', LABEL, { sub: 'RESET' });
  const ty = y0 + 256;
  c.key(id('play'), d, [x0, ty, x0 + 80, ty + 97], 'ss', 'START·STOP', LABEL, { toggle: true });
  c.key(id('sync'), d, [x0 + 86, ty, x0 + 140, ty + 45], 'sync', 'SYNC', LABEL);
  c.key(id('cue'), d, [x0 + 146, ty, x0 + 200, ty + 45], 'cue', 'CUE', LABEL);
  c.key(id('bend.down'), d, [x0 + 86, ty + 52, x0 + 140, ty + 97], 'w', '−', 14, { sub: 'BEND' });
  c.key(id('bend.up'), d, [x0 + 146, ty + 52, x0 + 200, ty + 97], 'w', '+', 14, { sub: 'BEND' });
}

function landscape(): PultBoardLayout {
  // The generator's board a point higher, into the 363 points between the safe areas.
  const c = new Sheet(-1);
  landDeck(c, 'A', 6, 6);
  c.sep(275, 6, 277, 359);
  c.sep(537, 6, 539, 359);
  mixer(c, 280, 6, 254, 353, false);
  landDeck(c, 'B', 542, 6);
  return { orientation: 'land', width: 814, height: 363, parts: Object.freeze(c.parts) };
}

/* ── phone upright: `portrait_b()`, 418 × 790 ──────────────────────────────── */

/** `p_deck_b`: one deck across the board — the slot, the pitch column, the transport block. */
function portDeck(c: Sheet, d: DeckSide, x0: number, y0: number): void {
  const id = (name: string): string => `${d}.${name}`;
  const s = 196;
  c.tabs(d, DECK_TABS, x0, y0, x0 + s, y0 + MIN);
  c.key(id('rpm33'), d, [x0 + s + 12, y0, x0 + s + 56, y0 + MIN], 'w', '33', 13, { toggle: true });
  c.key(id('rpm45'), d, [x0 + s + 60, y0, x0 + s + 104, y0 + MIN], 'w', '45', 13, { toggle: true });
  c.letter(x0 + s + 113, y0 + 11, d);
  c.key(id('keylock'), d, [x0 + 402 - 66, y0, x0 + 402, y0 + MIN], 'blue', 'KEYLOCK', SMALL);
  deckSlot(c, d, x0, y0 + 50, s);
  // pitch column: RESET on top, the fader under it
  const px = x0 + s + 8;
  c.key(id('reset'), d, [px, y0 + 50, px + 50, y0 + 94], 'w', '0 %', LABEL, { sub: 'RESET' });
  c.label(px + 45, y0 + 104, '−', LABEL);
  c.label(px + 45, y0 + 241, '+', LABEL);
  c.vfader(id('pitch'), d, px + 25, y0 + 100, y0 + 246, [MIN, 22, 4], 'l', [y0 + 104, y0 + 241]);
  // transport block
  const tx = px + 58;
  const tw = 402 - s - 8 - 58;
  const hw = (tw - 4) / 2;
  c.key(id('bend.down'), d, [tx, y0 + 50, tx + hw, y0 + 94], 'w', '−', 14, { sub: 'BEND' });
  c.key(id('bend.up'), d, [tx + hw + 4, y0 + 50, tx + tw, y0 + 94], 'w', '+', 14, { sub: 'BEND' });
  c.key(id('sync'), d, [tx, y0 + 100, tx + hw, y0 + 150], 'sync', 'SYNC', LABEL);
  c.key(id('cue'), d, [tx + hw + 4, y0 + 100, tx + tw, y0 + 150], 'cue', 'CUE', LABEL);
  c.key(id('play'), d, [tx, y0 + 156, tx + tw, y0 + 246], 'ss', 'START·STOP', 12, { toggle: true });
}

function portrait(): PultBoardLayout {
  const c = new Sheet(0);
  portDeck(c, 'A', 8, 8);
  c.sep(8, 257, 410, 259);
  c.sep(8, 531, 410, 533);
  mixer(c, 8, 262, 402, 266, true);
  portDeck(c, 'B', 8, 536);
  return { orientation: 'port', width: 418, height: 790, parts: Object.freeze(c.parts) };
}

/**
 * The two boards, built once and frozen: the board's controls are kept by the part they draw
 * (`memo` in `components/PultBoard.tsx`), so a part is the same object for the life of the app.
 */
export const PULT_LAYOUT: Readonly<Record<PultOrientation, PultBoardLayout>> = Object.freeze({
  land: Object.freeze(landscape()),
  port: Object.freeze(portrait()),
});
